import type { GetStaticProps, GetStaticPaths, NextPage } from 'next';
import type { SimplePage } from 'interfaces/content/articles/Post';
import type { HeaderNav } from 'interfaces/navigation/Header';
import { StaticPage } from 'pages/Static/StaticPage';
import getPageBySlug from 'api/Page/getPageByURI';
import type { PianoPageConfig } from 'services/Piano/entities/PianoPageConfig';
import { setMockPianoConfig } from 'services/Piano';
import type { ArcSpanPageConfig } from 'services/ArcSpan/entities';
import { setarcSpanPageConfig } from 'services/ArcSpan';
import getFooter from 'api/Footer/getFooter';
import { type HeadData, setHeadData } from 'utils/metaDataUtils/headData';
import handleStaticPropsError from 'utils/handleStaticPropsError';
import getMenu from 'api/Header/getMenu';
import setArticlePageAdConfig from 'services/Ad/config/setArticlePageAdConfig';
import { safe, safeAsync } from 'utils/errorHandlingUtils';
import fallbackData from 'lib/fallbackData/commonData';
import { clearHTMLString } from 'utils/stringUtils';

interface PageProps {
  page: SimplePage;
}

const StaticNextPage: NextPage<PageProps> = ({ page }: PageProps) => <StaticPage page={page} />;

export const getStaticProps: GetStaticProps = async (context) => {
  try {
    const { slug } = context.params as { slug: string };

    const page: SimplePage = await getPageBySlug(`franchise-list-page/${slug}`);
    const menu: HeaderNav = await safeAsync(
      () => getMenu('LEFT_MENU_COM', 'RIGHT_MENU_COM', 'TOPIC_NAVIGATION_COM', 'own', null),
      fallbackData.headerNav,
    );
    const footer = await safeAsync(() => getFooter(), fallbackData.footer);

    if (page.metaTitle) {
      page.title = page.metaTitle;
    }
    if (!page.description) {
      page.description = `${page.title} ${clearHTMLString(page.content).replace(/\n/g, ' ')}`.substring(0, 160);
    }
    if (!page.socialTitle) {
      page.socialTitle = page.title;
    }
    if (!page.socialDescription) {
      page.socialDescription = page.description;
    }

    const headData: HeadData = safe(() => setHeadData(page), fallbackData.headData);
    const arcSpanPageConfig: ArcSpanPageConfig = safe(() => setarcSpanPageConfig(page), fallbackData.arcSpanPageConfig);
    const pageAdConfig = safe(() => setArticlePageAdConfig(page), fallbackData.pageAdConfig);
    const pianoPageConfig: PianoPageConfig = safe(() => setMockPianoConfig(), fallbackData.pianoPageConfig);

    return {
      props: {
        arcSpanPageConfig,
        footer,
        headData,
        headerNav: menu,
        page,
        pageAdConfig,
        pianoPageConfig,
      },
      revalidate: 60,
    };
  } catch (e) {
    return handleStaticPropsError(e as Error);
  }
};

export const getStaticPaths: GetStaticPaths<{ slug: string }> = () => ({
  fallback: 'blocking',
  paths: [{ params: { slug: 'fortune-500-methodology-2022' } }],
});

export default StaticNextPage;
